import React, { useState, useRef } from 'react';
import { Upload, X, FileText, Image as ImageIcon } from 'lucide-react';
import { AttachmentService, Attachment } from '../../services/attachment.service';
import { Button } from '../shared';

interface FileUploadProps {
    ticketId: string;
    userId: string;
    attachments: Attachment[];
    onUploadSuccess: (attachment: Attachment) => void;
    onDeleteSuccess: (attachmentId: string) => void;
    onError: (message: string) => void;
}

export const FileUpload: React.FC<FileUploadProps> = ({
    ticketId,
    userId,
    attachments,
    onUploadSuccess,
    onDeleteSuccess,
    onError
}) => {
    const [isUploading, setIsUploading] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = async (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            await handleFiles(e.dataTransfer.files);
        }
    };

    const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        if (e.target.files && e.target.files[0]) {
            await handleFiles(e.target.files);
        }
    };

    const handleFiles = async (files: FileList) => {
        setIsUploading(true);
        try {
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const attachment = await AttachmentService.uploadFile(ticketId, file, userId);
                if (attachment) {
                    onUploadSuccess(attachment);
                }
            }
        } catch (error: any) {
            onError(error.message || 'Error al subir archivo');
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleDelete = async (attachmentId: string) => {
        if (!window.confirm('¿Está seguro de que desea eliminar este archivo?')) {
            return;
        }

        try {
            const success = await AttachmentService.deleteAttachment(attachmentId);
            if (success) {
                onDeleteSuccess(attachmentId);
            } else {
                onError('Error al eliminar archivo');
            }
        } catch (error: any) {
            onError(error.message || 'Error al eliminar archivo');
        }
    };

    const onButtonClick = () => {
        fileInputRef.current?.click();
    };

    return (
        <div className="space-y-4">
            {/* Upload Area */}
            <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                    } ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleChange}
                    className="hidden"
                />
                <Upload className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                <p className="text-sm text-gray-600 mb-2">
                    {isUploading ? 'Subiendo archivo...' : 'Arrastra archivos aquí o haz clic para seleccionar'}
                </p>
                <Button onClick={onButtonClick} size="sm" disabled={isUploading}>
                    Seleccionar archivos
                </Button>
                <p className="text-xs text-gray-500 mt-2">
                    Máx. 10MB - Imágenes, PDF, Word, Excel, TXT, CSV
                </p>
            </div>

            {/* Attachments List */}
            {attachments.length > 0 && (
                <div className="space-y-2">
                    <h4 className="text-sm font-bold text-gray-700">Archivos adjuntos</h4>
                    {attachments.map((attachment) => (
                        <div
                            key={attachment.id}
                            className="flex items-center justify-between p-3 bg-white border rounded-lg hover:bg-gray-50 transition"
                        >
                            <div className="flex items-center space-x-3 flex-1">
                                <span className="text-2xl">
                                    {AttachmentService.getFileIcon(attachment.fileType)}
                                </span>
                                <div className="flex-1 min-w-0">
                                    <a
                                        href={attachment.fileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm font-medium text-blue-600 hover:underline truncate block"
                                    >
                                        {attachment.fileName}
                                    </a>
                                    <p className="text-xs text-gray-500">
                                        {AttachmentService.formatFileSize(attachment.fileSize)} •{' '}
                                        {new Date(attachment.createdAt).toLocaleDateString()}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => handleDelete(attachment.id)}
                                className="ml-2 p-1 text-gray-400 hover:text-red-600 transition"
                                title="Eliminar archivo"
                            >
                                <X size={18} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
